import { useState } from 'react';
import { isOfficeAvailable } from '@slideforge/host-bridge';
import { useLocale, useStrings } from './i18n/context.js';
import { useSlideForge } from './state/useSlideForge.js';
import { initialTab, rememberTab, type Tab } from './state/tab.js';
import { DeckPanel } from './components/DeckPanel.js';
import { QuizPanel } from './components/QuizPanel.js';
import { NarrationPanel } from './components/NarrationPanel.js';
import { InteractionPanel } from './components/InteractionPanel.js';
import { PublishPanel } from './components/PublishPanel.js';

export interface AppProps {
  /** Lets tests and the browser preview render the pane outside PowerPoint. */
  readonly forceRender?: boolean;
}

export function App({ forceRender = false }: AppProps) {
  const t = useStrings();
  const locale = useLocale();
  // The ribbon selects the panel through the pane URL; see state/tab.ts.
  const [tab, setTab] = useState<Tab>(initialTab);
  const state = useSlideForge();

  // A tab chosen by hand is remembered the same way one chosen from the ribbon
  // is, so a pane rebuild does not throw the author back to the deck panel.
  const selectTab = (next: Tab) => {
    setTab(next);
    rememberTab(next);
  };

  if (!isOfficeAvailable() && !forceRender) {
    return (
      <main className="sf-app" lang={locale}>
        <h1>{t.notInPowerPoint}</h1>
        <p>{t.notInPowerPointBody}</p>
      </main>
    );
  }

  const tabs: readonly { id: Tab; label: string }[] = [
    { id: 'deck', label: t.tabDeck },
    { id: 'quiz', label: t.tabQuiz },
    { id: 'interaction', label: t.tabInteraction },
    { id: 'narration', label: t.tabNarration },
    { id: 'publish', label: t.tabPublish },
  ];

  return (
    <main className="sf-app" lang={locale}>
      <h1 className="sf-visually-hidden">{t.appName}</h1>

      {/*
        A real tablist, so a screen reader announces "tab 2 of 3" and arrow keys
        move between tabs. Buttons in a row look identical and behave nothing
        alike.
      */}
      <div role="tablist" aria-label={t.appName} className="sf-tabs">
        {tabs.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            role="tab"
            id={`sf-tab-${id}`}
            aria-selected={tab === id}
            aria-controls={`sf-tabpanel-${id}`}
            tabIndex={tab === id ? 0 : -1}
            className={tab === id ? 'sf-tab sf-tab-current' : 'sf-tab'}
            onClick={() => selectTab(id)}
            onKeyDown={(event) => {
              const index = tabs.findIndex((x) => x.id === tab);
              if (event.key === 'ArrowRight') selectTab(tabs[(index + 1) % tabs.length]?.id ?? tab);
              if (event.key === 'ArrowLeft') selectTab(tabs[(index - 1 + tabs.length) % tabs.length]?.id ?? tab);
            }}
          >
            {label}
          </button>
        ))}
      </div>

      <SaveIndicator state={state} />
      <StoreMessages state={state} />

      {tabs.map(({ id }) => (
        <div
          key={id}
          role="tabpanel"
          id={`sf-tabpanel-${id}`}
          aria-labelledby={`sf-tab-${id}`}
          hidden={tab !== id}
          tabIndex={0}
        >
          {id === 'deck' && <DeckPanel state={state} />}
          {id === 'quiz' && <QuizPanel state={state} />}
          {id === 'interaction' && <InteractionPanel state={state} />}
          {id === 'narration' && <NarrationPanel state={state} />}
          {id === 'publish' && <PublishPanel state={state} />}
        </div>
      ))}
    </main>
  );
}

/**
 * Whether the author's work is safe, stated at all times.
 *
 * Writes are debounced and land inside the .pptx, so there is no moment the
 * author can point at and say "that saved". Without a status line the only
 * feedback is absence of an error, which is indistinguishable from the add-in
 * quietly doing nothing — and that is precisely how it was reported.
 *
 * The note about Ctrl+S is not decoration. `settings.saveAsync` writes into the
 * open document, not to disk; closing PowerPoint without saving the file
 * discards it, and nothing in Office says so.
 */
function SaveIndicator({ state }: { readonly state: ReturnType<typeof useSlideForge> }) {
  const t = useStrings();
  const { saveStatus, flushNow } = state;

  const label =
    saveStatus === 'pending' ? t.savePending : saveStatus === 'error' ? t.saveFailed : t.saved;

  return (
    <div className="sf-savebar">
      <span
        className={`sf-savedot sf-savedot-${saveStatus}`}
        role="status"
        aria-live="polite"
        data-testid="save-status"
      >
        {label}
      </span>
      {saveStatus === 'pending' || saveStatus === 'error' ? (
        <button type="button" className="sf-linkbutton" onClick={() => void flushNow()}>
          {t.saveNow}
        </button>
      ) : null}
      <p className="sf-savenote">{t.savedInDeckNote}</p>
    </div>
  );
}

/**
 * Store feedback the author has to see: a quota warning, a co-authoring
 * clobber, a failed save. Silence here is how a lecturer loses a quiz.
 */
function StoreMessages({ state }: { readonly state: ReturnType<typeof useSlideForge> }) {
  const t = useStrings();
  const latest = [...state.storeEvents]
    .reverse()
    .find((e) => e.kind === 'quota-warning' || e.kind === 'clobber-detected' || e.kind === 'error');

  if (!latest) return null;

  const message =
    latest.kind === 'quota-warning'
      ? `${t.quotaWarning} ${latest.message}`
      : latest.kind === 'clobber-detected'
        ? `${t.clobberWarning} ${latest.message}`
        : `${t.saveFailed}: ${latest.message}`;

  return (
    <p role="alert" className={latest.kind === 'error' ? 'sf-error' : 'sf-warning'}>
      {message}
    </p>
  );
}
